import {useDispatch} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";
import {number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {BrandPhoneIcon} from "@pg-design/icons-module";
import {FieldWrapper} from "@pg-design/inputs-module";
import {Modal} from "@pg-design/modal-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "../../../notifications/utils/error_notifications";
import {useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../../user/api/patch_user_profile";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
    onSkip?: () => void;
}

interface IFormData {
    name: string;
    phone: {
        code: number;
        number: string;
    };
}

const validationSchema = Yup.object().shape({
    name: string().required("Podaj imię i nazwisko"),
    phone: object().shape({
        code: number().required(),
        number: string()
            .required("Podaj numer telefonu")
            .matches(/^\d+$/, "Podaj poprawny numer telefonu")
            .min(9, "Numer telefonu musi mieć 9 cyfr")
            .max(9, "Numer telefonu musi mieć 9 cyfr")
    })
});

export const RegistrationCompletionModal = (props: IProps) => {
    const {isOpen, onSuccess, onSkip} = props;
    const dispatch = useDispatch();

    const {data: userProfile} = useGetUserProfileQuery();
    const countryCodes = useAppSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);
    const [patchUserProfile] = usePatchUserProfileMutation();

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            name: userProfile?.name || "",
            phone: {
                code: 48,
                number: ""
            }
        }
    });

    const onSubmit = async (values: IFormData) => {
        const response = await patchUserProfile({
            name: values.name,
            phone: `${values.phone.code} ${values.phone.number}`
        });

        if ("error" in response) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            form.setError("phone", {
                type: "manual",
                message: "Coś poszło nie tak, spróbuj ponownie"
            });
        } else {
            onSuccess?.();
        }
    };

    const onModalClose = () => {
        onSkip?.();
    };

    return (
        <Modal onModalClose={onModalClose} isOpen={isOpen}>
            <Modal.Layout.EmptyResponsive closeButtonVariant="rounded" className={modalStyle}>
                <div className={titleStyle}>
                    <BrandPhoneIcon size="4" wrapperSize="4" wrapperColor="var(--colors-primary)" />
                    <Text variant="headline_4">Podaj numer telefonu, aby nic Cię nie ominęło</Text>
                </div>
                <Text variant="body_copy_1" strong>
                    Dzięki numerowi telefonu będziemy mogli:
                </Text>
                <Text as="ul" variant="body_copy_1" className={listStyle}>
                    <li>wysyłać Ci SMS-y o nowych mieszkaniach dopasowanych do Twoich preferencji</li>
                    <li>przekazać Twój kontakt deweloperom, aby mogli szybko odpowiedzieć na Twoje zapytania</li>
                </Text>
                <FormProviderRHF {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <FieldWrapper labelContent="Imię i nazwisko">
                            <InputField name="name" placeholder="Imię i nazwisko" className={inputFieldWrapperStyle} />
                        </FieldWrapper>
                        <FieldWrapper labelContent="Numer telefonu">
                            <PhoneFieldControlled
                                name="phone"
                                codes={countryCodes}
                                placeholder="Numer telefonu"
                                maxMenuHeight={150}
                                className={inputFieldWrapperStyle}
                            />
                        </FieldWrapper>
                        <Button variant="filled_primary" type="submit" fullWidth>
                            Zatwierdź
                        </Button>
                    </form>
                </FormProviderRHF>
            </Modal.Layout.EmptyResponsive>
        </Modal>
    );
};

const titleStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    mb: 2,
    mt: 1,
    pr: 4,

    md: {
        mt: 0
    }
});

const modalStyle = css({
    flexDirection: "column",
    p: 3,
    md: {
        p: 5
    }
});

const listStyle = css({
    mb: 6
});

const inputFieldWrapperStyle = css({
    mb: 4
});
